import React, { useState } from 'react';
import { bannerTone, clearPalletProblem, clearProblemError, warningParts, whoWhen } from './palletCheckData';
import { C } from './styles';

// The top banner (the same decision as the printed pallet label and the beacon screen: banner.state from
// the API, its words shown as they come), the warnings under it, and the open problems.

const Triangle = ({ color = C.red }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }}>
    <path d="M12 3l10 18H2L12 3z" /><path d="M12 10v5M12 18h.01" />
  </svg>
);

export function Banner({ banner }) {
  if (!banner) return null;
  const t = bannerTone(banner.state);
  return (
    <div data-testid="pallet-banner" data-state={banner.state}
      style={{ margin: '14px 16px 0', background: t.background, color: t.color, border: t.border, borderRadius: 14, padding: 16,
        display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 30, fontWeight: 700, letterSpacing: '0.01em', color: t.titleColor, lineHeight: 1.1 }}>{banner.title}</div>
        {banner.tag && (
          <div style={{ fontSize: 13, fontWeight: 700, background: t.tagBackground, color: t.tagColor, borderRadius: 6, padding: '4px 8px' }}>{banner.tag}</div>
        )}
      </div>
      {banner.instruction && <div style={{ fontSize: 17, fontWeight: 600 }}>{banner.instruction}</div>}
      {banner.detail && <div style={{ fontSize: 14 }}>{banner.detail}</div>}
    </div>
  );
}

function WarningCard({ w }) {
  const { lead, rest } = warningParts(w.message);
  return (
    <div data-code={w.code} data-testid="pallet-warning"
      style={{ background: '#FFFFFF', border: `2px solid ${C.red}`, borderRadius: 12, padding: '12px 14px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <Triangle />
      <div style={{ fontSize: 15 }}><b>{lead}</b>{rest ? ` ${rest}` : ''}</div>
    </div>
  );
}

function ClearForm({ palletId, problem, onCleared }) {
  const [pin, setPin] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    const bad = clearProblemError({ pin, note });
    if (bad) { setError(bad); return; }
    setBusy(true); setError(null);
    const r = await clearPalletProblem(palletId, problem.check_id, { pin, note });
    setBusy(false);
    if (!r.ok) { setError(r.message); return; }
    setPin('');
    onCleared(r.body && r.body.check);
  };
  const ids = `clear-${problem.check_id}`;
  return (
    <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10, borderTop: `1px solid ${C.track}`, paddingTop: 10 }}>
      <label htmlFor={`${ids}-note`} style={{ fontSize: 14, fontWeight: 600 }}>What was done</label>
      <textarea id={`${ids}-note`} rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000}
        style={{ borderRadius: 10, border: `1px solid ${C.steel}`, padding: '8px 12px', fontFamily: 'inherit', fontSize: 16 }} />
      <label htmlFor={`${ids}-pin`} style={{ fontSize: 14, fontWeight: 600 }}>Manager PIN</label>
      <input id={`${ids}-pin`} type="password" inputMode="numeric" autoComplete="off" value={pin} onChange={(e) => setPin(e.target.value)}
        style={{ minHeight: 48, borderRadius: 10, border: `1px solid ${C.steel}`, padding: '0 12px', fontSize: 18, letterSpacing: '0.3em' }} />
      {error && <div role="status" data-testid="clear-error" style={{ color: C.red, fontWeight: 600, fontSize: 14 }}>{error}</div>}
      <button type="submit" disabled={busy}
        style={{ minHeight: 48, borderRadius: 10, border: 0, background: C.ink, color: '#FFFFFF', fontFamily: 'inherit', fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>
        {busy ? 'Clearing…' : 'Clear the problem'}
      </button>
    </form>
  );
}

function ProblemCard({ palletId, problem, onCleared, onPhoto }) {
  const [clearing, setClearing] = useState(false);
  return (
    <div data-testid={`open-problem-${problem.check_id}`}
      style={{ background: '#FEF2F2', border: `2px solid ${C.red}`, borderRadius: 12, padding: '12px 14px', color: C.ink }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <Triangle />
        <div style={{ flexGrow: 1, minWidth: 0, fontSize: 15 }}>
          <div><b>Problem reported: {problem.problem_label || problem.problem_kind || 'something is wrong'}.</b></div>
          <div style={{ fontSize: 14, color: C.sub }}>{whoWhen(problem)}</div>
          {problem.counted_qty != null && <div>They counted {problem.counted_qty}.</div>}
          {problem.note && <div style={{ overflowWrap: 'anywhere' }}>“{problem.note}”</div>}
        </div>
        {problem.photo_url && (
          <button type="button" onClick={() => onPhoto(problem.photo_url)} aria-label="Enlarge the problem photo"
            style={{ flexShrink: 0, width: 64, height: 64, padding: 0, border: `1px solid ${C.dim}`, borderRadius: 8, overflow: 'hidden', background: '#FFFFFF', cursor: 'pointer' }}>
            <img src={problem.photo_url} alt="What they saw" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </button>
        )}
      </div>
      {!clearing && (
        <button type="button" onClick={() => setClearing(true)}
          style={{ marginTop: 10, minHeight: 44, width: '100%', borderRadius: 10, border: `1px solid ${C.red}`, background: '#FFFFFF', color: C.red,
            fontFamily: 'inherit', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
          Clear (manager)
        </button>
      )}
      {clearing && <ClearForm palletId={palletId} problem={problem} onCleared={onCleared} />}
    </div>
  );
}

/**
 * Everything under the banner that needs attention, in ONE role="alert" region: the open problems first
 * (with their photo and the manager's Clear), then the warnings. The API's own open_problem warning is
 * left out while the problem itself is listed (it would say it twice).
 */
export function Alerts({ palletId, problems, warnings, onCleared, onPhoto }) {
  const open = problems || [];
  const list = (warnings || []).filter((w) => w && w.message && !(open.length && w.code === 'open_problem'));
  if (!open.length && !list.length) return null;
  return (
    <div role="alert" data-testid="pallet-alerts" style={{ margin: '10px 16px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
      {open.map((p) => <ProblemCard key={p.check_id} palletId={palletId} problem={p} onCleared={onCleared} onPhoto={onPhoto} />)}
      {list.map((w, i) => <WarningCard key={`${w.code}-${i}`} w={w} />)}
    </div>
  );
}
